import { getCookie } from './cookies';
import { getCsrfToken } from '../lib/apiClient';
import { reportSessionExpired } from './sessionExpiry';

const NON_SAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Дефолтный бюджет ожидания ОТВЕТА сервера (до прихода заголовков).
// Это детектор зависшего запроса, а не SLA: без него зависший запрос держит спиннер
// страницы бесконечно (кнопка «в процессе», пользователь ждёт молча).
// Тело ответа таймером НЕ ограничено — как только Response получен, таймер снимается,
// поэтому долгая выкачка ZIP/PDF не рвётся.
export const DEFAULT_TIMEOUT_MS = 120_000;

// init.timeoutMs:
//   не задан  — DEFAULT_TIMEOUT_MS (кроме запросов с бинарным телом, см. isStreamedBody)
//   число > 0 — свой бюджет в мс
//   null / 0  — таймаут выключен (операции, длительность которых заведомо не ограничена)
export type CsrfFetchInit = RequestInit & { timeoutMs?: number | null };

// Таймаут кидает Error с name='TimeoutError' (НЕ AbortError), чтобы обработчики,
// которые молча глотают отмену пользователя (`e.name === 'AbortError'`), не съели его.
export const isTimeoutError = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as { name?: string }).name === 'TimeoutError';

// Длительность заливки файла определяется размером тела и шириной канала, а не
// отзывчивостью сервера — фиксированный латентный бюджет тут неприменим.
const isStreamedBody = (body: BodyInit | null | undefined): boolean =>
  (typeof FormData !== 'undefined' && body instanceof FormData) ||
  (typeof Blob !== 'undefined' && body instanceof Blob) ||
  (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
  ArrayBuffer.isView(body as ArrayBufferView) ||
  (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);

const resolveTimeout = (
  timeoutMs: number | null | undefined,
  body: BodyInit | null | undefined
): number => {
  if (timeoutMs === null) return 0;
  if (typeof timeoutMs === 'number') return timeoutMs > 0 ? timeoutMs : 0;
  return isStreamedBody(body) ? 0 : DEFAULT_TIMEOUT_MS;
};

const makeTimeoutError = (input: RequestInfo | URL, budgetMs: number): Error => {
  const seconds = Math.round(budgetMs / 1000);
  const err = new Error(
    `Сервер не ответил за ${seconds} с — запрос прерван по таймауту. Повторите попытку.`
  );
  err.name = 'TimeoutError';
  (err as Error & { url?: string }).url = String(input).slice(0, 200);
  return err;
};

// Обёртка над fetch:
// - всегда отправляет cookies (`credentials: 'include'`)
// - для не-GET методов добавляет заголовок `X-CSRFToken`: из cookie `csrftoken` (веб),
//   а если cookie недоступна (desktop-EXE на file://) — из памяти apiClient (тело /api/csrf/).
// - ограничивает ожидание ответа таймаутом (см. DEFAULT_TIMEOUT_MS / init.timeoutMs);
//   собственный init.signal вызывающего продолжает работать — прерывает любой из двух.
// Пользовательские опции (включая собственный заголовок X-CSRFToken и credentials) имеют приоритет.
export const csrfFetch = (
  input: RequestInfo | URL,
  init: CsrfFetchInit = {}
): Promise<Response> => {
  const { timeoutMs, ...rest } = init;
  const method = (rest.method || 'GET').toUpperCase();
  const headers = new Headers(rest.headers || {});
  if (NON_SAFE_METHODS.has(method) && !headers.has('X-CSRFToken')) {
    const token = getCookie('csrftoken') || getCsrfToken();
    if (token) headers.set('X-CSRFToken', token);
  }

  const budgetMs = resolveTimeout(timeoutMs, rest.body);
  const external = rest.signal ?? undefined;

  let signal: AbortSignal | undefined = external;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onExternalAbort: (() => void) | undefined;
  let timedOut = false;

  if (budgetMs > 0) {
    const controller = new AbortController();
    signal = controller.signal;
    // Свой сигнал вызывающего не теряется: пробрасываем его отмену в наш контроллер
    // с ЕГО же reason — снаружи это по-прежнему обычный AbortError.
    const reasonOf = (s: AbortSignal) => (s as AbortSignal & { reason?: unknown }).reason;
    if (external) {
      if (external.aborted) {
        controller.abort(reasonOf(external));
      } else {
        onExternalAbort = () => controller.abort(reasonOf(external));
        external.addEventListener('abort', onExternalAbort);
      }
    }
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, budgetMs);
  }

  const cleanup = () => {
    if (timer !== undefined) clearTimeout(timer);
    if (external && onExternalAbort) external.removeEventListener('abort', onExternalAbort);
  };

  return fetch(input, {
    ...rest,
    credentials: rest.credentials ?? 'include',
    headers,
    signal,
  })
    .then((res) => {
      // Ответ получен — дальше тело льётся сколько нужно, таймер больше не тикает.
      cleanup();
      // Истёкшая сессия: глобальный репорт (AuthContext сбросит isAuthenticated,
      // поллеры/стримы остановятся). /api/login/ — там 401 = неверный пароль.
      if (res.status === 401 && !String(input).includes('/api/login')) {
        reportSessionExpired('csrfFetch:' + String(input).slice(0, 80));
      }
      return res;
    })
    .catch((err) => {
      cleanup();
      if (timedOut) throw makeTimeoutError(input, budgetMs);
      throw err;
    });
};
